import React from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";

import { DateTimeFieldBase } from "@/src/components/form-fields/DateTimeField";
import { SegmentedFieldBase } from "@/src/components/form-fields/SegmentedField";
import { SelectFieldBase } from "@/src/components/form-fields/SelectField";
import { SwitchFieldBase } from "@/src/components/form-fields/SwitchField";
import { TextFieldBase } from "@/src/components/form-fields/TextField";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";

import {
  getLiveQualities,
  isValidLiveDuration,
  LIVE_DURATION_MAX_MINUTES,
  LIVE_DURATION_MIN_MINUTES,
} from "../../utils/liveQuota";
import { parseUtcMs } from "../../utils/liveView";

import type { JSX } from "react";

import type { LiveSettings } from "../../utils/liveSettings";

interface LiveSettingsCardProps {
  settings: LiveSettings;
  /** Editable form while true, locked summary otherwise. */
  isEditable: boolean;
  showSource: boolean;
  showQuality: boolean;
  /** organization_options.allow_replay: hides the replay switch when false. */
  showReplay: boolean;
  liveEncodingPlan: string;
  onChange: (patch: Partial<LiveSettings>) => void;
}

const SOURCE_OPTION_LIST = [
  { value: "rtmp", label: "RTMP" },
  { value: "srt", label: "SRT" },
];

interface SummaryRowProps {
  label: string;
  value: string;
}

function SummaryRow({ label, value }: SummaryRowProps): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function LiveSettingsCard({
  settings,
  isEditable,
  showSource,
  showQuality,
  showReplay,
  liveEncodingPlan,
  onChange,
}: LiveSettingsCardProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const minutes = Math.round(settings.durationSeconds / 60);
  const isDurationValid = isValidLiveDuration(minutes);
  const startMs = parseUtcMs(settings.scheduledStart);
  const startDate = startMs === null ? undefined : new Date(startMs);

  const qualityOptionList = getLiveQualities(liveEncodingPlan).map(
    (quality) => ({ value: quality, label: `${quality}p` }),
  );

  if (!isEditable) {
    return (
      <Card className="gap-4 py-4">
        <CardHeader className="px-4">
          <CardTitle>{t("liveRegie_settingsTitle")}</CardTitle>
          <CardDescription>{t("liveRegie_settingsLocked")}</CardDescription>
        </CardHeader>
        <CardContent className="px-4">
          <dl className="flex flex-col gap-2">
            {showSource ? (
              <SummaryRow
                label={t("liveRegie_source")}
                value={settings.inputType.toUpperCase()}
              />
            ) : null}
            <SummaryRow
              label={t("liveRegie_duration")}
              value={t("liveRegie_minutes", { minutes })}
            />
            {showQuality ? (
              <SummaryRow
                label={t("liveRegie_maxQuality")}
                value={`${settings.maxResolution}p`}
              />
            ) : null}
            {showReplay ? (
              <SummaryRow
                label={t("liveRegie_replay")}
                value={t(
                  settings.instantReplay
                    ? "liveRegie_replayEnabled"
                    : "liveRegie_replayDisabled",
                )}
              />
            ) : null}
          </dl>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="gap-4 py-4">
      <CardHeader className="px-4">
        <CardTitle>{t("liveRegie_settingsTitle")}</CardTitle>
        <CardDescription>{t("liveRegie_settingsEditable")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-4">
        {showSource ? (
          <SegmentedFieldBase
            label={t("liveRegie_source")}
            options={SOURCE_OPTION_LIST}
            value={settings.inputType}
            onValueChange={(value) => {
              if (value === "rtmp" || value === "srt")
                onChange({ inputType: value });
            }}
          />
        ) : null}

        <TextFieldBase
          label={t("liveRegie_durationMinutes")}
          type="number"
          value={Number.isNaN(minutes) ? "" : String(minutes)}
          error={
            isDurationValid
              ? undefined
              : t("liveRegie_durationError", {
                  min: LIVE_DURATION_MIN_MINUTES,
                  max: LIVE_DURATION_MAX_MINUTES,
                })
          }
          onChange={(value) =>
            onChange({ durationSeconds: Number(value) * 60 })
          }
        />

        {showQuality ? (
          <SelectFieldBase
            label={t("liveRegie_maxQuality")}
            onValueChange={(value) => onChange({ maxResolution: value })}
            options={qualityOptionList}
            value={settings.maxResolution}
          />
        ) : null}

        <SwitchFieldBase
          checked={settings.scheduled}
          label={t("liveRegie_scheduleToggle")}
          onCheckedChange={(checked) => onChange({ scheduled: checked })}
          variant="inline"
        />
        {settings.scheduled ? (
          <DateTimeFieldBase
            label={t("liveRegie_scheduledStart")}
            minDate={new Date()}
            value={startDate}
            onChange={(date) =>
              onChange({ scheduledStart: moment.utc(date).format() })
            }
          />
        ) : null}

        {showReplay ? (
          <SwitchFieldBase
            checked={settings.instantReplay}
            label={t("liveRegie_replayEnabledLabel")}
            onCheckedChange={(checked) => onChange({ instantReplay: checked })}
            variant="inline"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

export default LiveSettingsCard;
